/**
 * HOOK: usePaymentActions
 * Actions mutation paiement : confirmation manuelle virement/chèque
 *
 * Depuis review finding C (2026-04-19) : confirmPayment délègue à la RPC
 * `confirm_payment_with_timeline` (migration 00068) pour garantir l'atomicité
 * UPDATE payment_cases + INSERT timeline_events. Si la timeline échoue, le
 * UPDATE est rollbacké — plus de paiement confirmé sans trace.
 */

import { useCallback } from 'react';
import { useSupabase } from './useSupabase';

interface ConfirmPaymentParams {
  paymentCaseId: string;
  comment: string;
  contactId: string;
  // Contact identifié comme payeur (TICKET-PAY-01 — review-blocker-01).
  // Obligatoire : persisté dans payment_cases.payer_contact_id et dans le
  // metadata de l'event timeline PAYMENT_PAID pour cohérence audit.
  payerContactId: string;
}

export interface ConfirmPaymentResult {
  success: boolean;
  // Message d'erreur traductible remonte a l'UI quand success=false.
  // Ex : conflit de payeur detecte par la garde anti-overwrite (review-blocker-A2).
  error?: string;
}

interface RestorePaymentParams {
  paymentCaseId: string;
  // Motif obligatoire trace en timeline (event payment_restored, migration 00127).
  reason: string;
}

// Mapping code erreur PG (RAISE EXCEPTION dans la RPC) → message UI.
function mapRpcError(message: string): ConfirmPaymentResult {
  // Migration 00106 : nouvelles gardes statut deja terminal.
  if (message.includes('payment_already_paid')) {
    return {
      success: false,
      error: 'Ce paiement a déjà été confirmé. Veuillez rafraîchir la page.',
    };
  }
  if (message.includes('payment_status_terminal')) {
    return {
      success: false,
      error:
        'Ce dossier est dans un statut terminal (expiré ou échoué) et ne peut plus être confirmé manuellement.',
    };
  }
  // Migration 00123 : allowlist statut. Seuls virement/chèque/CB en attente sont
  // confirmables manuellement ; un partial (BED) ou draft est rejeté côté serveur.
  if (message.includes('payment_not_confirmable')) {
    return {
      success: false,
      error:
        "Ce dossier n'est pas en attente d'une confirmation manuelle (seuls un virement, un chèque ou une CB en attente peuvent être confirmés).",
    };
  }
  if (message.includes('manual_card_confirmation_bpi_only')) {
    return {
      success: false,
      error:
        "La régularisation CB ancienne plateforme ne s'applique qu'aux BPI, pas aux dossiers BED.",
    };
  }
  if (message.includes('manual_card_confirmation_forbidden')) {
    return {
      success: false,
      error: 'Seule la compta peut confirmer manuellement un paiement CB.',
    };
  }
  if (message.includes('payment_card_method_required')) {
    return {
      success: false,
      error: "Ce dossier n'est pas un paiement CB en attente.",
    };
  }
  // Migrations 00074/00075 : validations contact/payeur membre du dossier.
  if (message.includes('contact_not_in_case')) {
    return {
      success: false,
      error: "Le contact sélectionné n'appartient pas à ce dossier.",
    };
  }
  if (message.includes('payer_contact_id_not_in_case')) {
    return {
      success: false,
      error: "Le payeur sélectionné n'appartient pas à ce dossier.",
    };
  }
  // Defense-in-depth (devient inatteignable avec 00106 mais conserve pour filet).
  if (message.includes('payer_conflict')) {
    return {
      success: false,
      error: 'Le dossier a changé de statut (payé, expiré ou échoué). Veuillez rafraîchir la page.',
    };
  }
  if (message.includes('authentication_required')) {
    return { success: false, error: 'Session expirée. Veuillez vous reconnecter.' };
  }
  if (message.includes('payment_case_not_found')) {
    return { success: false, error: 'Dossier de paiement introuvable.' };
  }
  if (message.includes('comment_required')) {
    return { success: false, error: 'Commentaire obligatoire.' };
  }
  if (message.includes('payer_required')) {
    return { success: false, error: 'Payeur obligatoire.' };
  }
  return { success: false };
}

// Mapping des codes d'erreur de restore_expired_payment_case (migration 00127)
// vers messages UI. Exporte pour test unitaire (pas de dependance React).
export function mapRestoreError(message: string): ConfirmPaymentResult {
  // current_user_role() hors admin/superadmin -> 42501. Couvre aussi le cas non
  // authentifie (role null -> NOT IN (...) -> access_denied).
  if (message.includes('access_denied')) {
    return {
      success: false,
      error: "Vous n'avez pas les droits pour ré-ouvrir un dossier (réservé aux administrateurs).",
    };
  }
  if (message.includes('reason_required')) {
    return { success: false, error: 'Motif obligatoire.' };
  }
  // Course : un autre admin a déjà restauré, ou le dossier n'a jamais été expiré.
  if (message.includes('payment_not_expired')) {
    return {
      success: false,
      error: "Ce dossier n'est pas (ou plus) expiré. Veuillez rafraîchir la page.",
    };
  }
  if (message.includes('payment_case_not_found')) {
    return { success: false, error: 'Dossier de paiement introuvable.' };
  }
  return { success: false };
}

export function usePaymentActions() {
  const supabase = useSupabase();

  const confirmPayment = useCallback(
    async ({
      paymentCaseId,
      comment,
      contactId,
      payerContactId,
    }: ConfirmPaymentParams): Promise<ConfirmPaymentResult> => {
      try {
        // RPC atomique : UPDATE payment_cases + INSERT timeline_events
        // dans une seule transaction (migration 00068, review finding C).
        const { error } = await supabase.rpc('confirm_payment_with_timeline', {
          p_payment_case_id: paymentCaseId,
          p_payer_contact_id: payerContactId,
          p_comment: comment,
          p_contact_id: contactId,
        });

        if (error) {
          console.error('[usePaymentActions] confirmPayment RPC error:', error);
          return mapRpcError(error.message ?? '');
        }

        return { success: true };
      } catch (err) {
        console.error('[usePaymentActions] confirmPayment error:', err);
        return { success: false };
      }
    },
    [supabase]
  );

  // Restauration manuelle d'un dossier expire (admin/superadmin). Delegue a la
  // RPC restore_expired_payment_case (00127) : expired -> draft, reset compteurs
  // de relance, re-active le crew archive, trace timeline_event payment_restored.
  // La garde de role est ENFORCEE cote serveur (current_user_role) ; le gating UI
  // n'est qu'un confort.
  const restoreExpiredPaymentCase = useCallback(
    async ({ paymentCaseId, reason }: RestorePaymentParams): Promise<ConfirmPaymentResult> => {
      try {
        const { error } = await supabase.rpc('restore_expired_payment_case', {
          p_payment_case_id: paymentCaseId,
          p_reason: reason,
        });

        if (error) {
          console.error('[usePaymentActions] restoreExpiredPaymentCase RPC error:', error);
          return mapRestoreError(error.message ?? '');
        }

        return { success: true };
      } catch (err) {
        console.error('[usePaymentActions] restoreExpiredPaymentCase error:', err);
        return { success: false };
      }
    },
    [supabase]
  );

  return { confirmPayment, restoreExpiredPaymentCase };
}
